import { handleApiError } from '@/shared/utils/handleApiError'
import { useAsyncData } from '@/shared/composables/useAsyncData'
import { apiFacility } from '../controllers/facility.api'
import { useStoreFacility } from '@/stores/facility'
import type { Uuid4 } from '§C/types/system.type'
import { ResponseFacilityDto } from '§A/facility/dto/response-facility.dto'
import { CreateFacilityDto } from '§A/facility/dto/create-facility.dto'
import { UpdateFacilityDto } from '§A/facility/dto/update-facility.dto'
import { ConnectFacilityProjectDto } from '§A/facility/dto/connect-facility-project.dto'

/**
 * Facility Service Layer
 *
 * Provides high-level business logic for facility operations.
 * Handles API calls, state management, error handling, and user notifications.
 */
export const useServiceFacility = () => {
  const {
    facilityReadAll,
    facilityReadOne,
    facilityCreate,
    facilityUpdate,
    facilityConnectToProject,
    facilityDisconnectFromProject
  } = apiFacility

  const storeFacility = useStoreFacility()

  /**
   * Read all facilities and update the store
   */
  const readAllFacilities = () => {
    return useAsyncData(async () => {
      const response = await facilityReadAll()

      // Update the store with fetched facilities
      storeFacility.setFacilities(response.facilities)

      return response
    })
  }


  /**
   * Read a single facility by ID
   */
  const readFacility = (facilityId: Uuid4) => {
    return useAsyncData(async () => {
      const response = await facilityReadOne(facilityId)

      // Update the store with the facility
      storeFacility.updateFacility(response.facility)

      return response
    })
  }

  /**
   * Create a new facility
   */
  const createFacility = async (createDto: CreateFacilityDto) => {
    try {
      const response = await facilityCreate(createDto)

      // Add to store
      storeFacility.addFacility(response.facility)

      return { data: response, error: null }
    } catch (error) {
      handleApiError(error)
      return { data: null, error }
    }
  }

  /**
   * Update an existing facility
   */
  const updateFacility = async (facilityId: Uuid4, updateDto: UpdateFacilityDto) => {
    try {
      const response = await facilityUpdate(facilityId, updateDto)

      // Update in store
      storeFacility.updateFacility(response.facility)

      return { data: response, error: null }
    } catch (error) {
      handleApiError(error)
      return { data: null, error }
    }
  }

  /**
   * Connect multiple facilities to a project
   * This will be used after project creation to connect selected facilities
   */
  const connectFacilitiesToProject = async (facilityIds: Uuid4[], projectId: Uuid4) => {
    const errors: any[] = []
    const successes: ResponseFacilityDto[] = []

    for (const facilityId of facilityIds) {
      try {
        const connectDto: ConnectFacilityProjectDto = { projectId }
        const response = await facilityConnectToProject(facilityId, connectDto)
        successes.push(response)

        // Update facility in store
        storeFacility.updateFacility(response.facility)
      } catch (error) {
        handleApiError(error)
        errors.push({ facilityId, error })
      }
    }

    return { successes, errors }
  }

  /**
   * Disconnect multiple facilities from a project
   */
  const disconnectFacilitiesFromProject = async (facilityIds: Uuid4[], projectId: Uuid4) => {
    const errors: any[] = []
    const successes: ResponseFacilityDto[] = []

    for (const facilityId of facilityIds) {
      try {
        const response = await facilityDisconnectFromProject(facilityId, projectId)
        successes.push(response)

        // Update facility in store
        storeFacility.updateFacility(response.facility)
      } catch (error) {
        handleApiError(error)
        errors.push({ facilityId, error })
      }
    }

    return { successes, errors }
  }

  /**
   * Make a project's facility links match `facilityIds` exactly, connecting the
   * ones that are missing and disconnecting the ones no longer selected.
   *
   * Editing locations on an existing collection has to reconcile rather than only
   * connect, or a removal is silently dropped.
   */
  const syncFacilitiesForProject = async (facilityIds: Uuid4[], currentFacilityIds: Uuid4[], projectId: Uuid4) => {
    const selected = new Set(facilityIds)
    const current = new Set(currentFacilityIds)

    const toConnect = facilityIds.filter((id) => !current.has(id))
    const toDisconnect = currentFacilityIds.filter((id) => !selected.has(id))

    const { errors: connectErrors } = await connectFacilitiesToProject(toConnect, projectId)
    const { errors: disconnectErrors } = await disconnectFacilitiesFromProject(toDisconnect, projectId)

    return { errors: [...connectErrors, ...disconnectErrors] }
  }

  return {
    readAllFacilities,
    readFacility,
    createFacility,
    updateFacility,
    syncFacilitiesForProject,
    connectFacilitiesToProject,
    disconnectFacilitiesFromProject
  }
}
